import { useApolloClient } from '@apollo/client'
import { useState } from 'react'
import { useStore } from 'react-redux'
import { RootState, useAppDispatch } from '../../App.store'
import { emptyArr } from '../../constants/misc'
import { DraftMatrixItem } from '../../data/gql-gen/questionnaire/graphql'
import { DRAFT_MATRIX_ITEM } from '../../data/gql/questionnaire/fragments/draftMatrixItem'
import { PositionTextSelection } from '../../data/model/questionnaire'
import { responseOptionPasted } from '../../modules/Questionnaire/Questionnaire.slice'
import { usePasteMutations } from '../questionnaire/usePasteMutations'
import { replacingTextAt, splitTextIntoLinesAndCleanup } from './utils'

interface UseCopyPasteCompleteData {
  // This one takes a text and formats and adds response options. Also cleans up
  pasteToMatrixRow: (
    text: string,
    matrixTitleLk: string,
    position: number,
    positionSelectedText?: PositionTextSelection
  ) => Promise<void>
  pasteToMatrixColumn: (params: {
    text: string
    matrixTitleLk: string
    position: number
    positionSelectedText?: PositionTextSelection
  }) => Promise<void>
  loading: boolean
}

const useCopyPasteMatrix: () => UseCopyPasteCompleteData = () => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const apolloClient = useApolloClient()
  const [loading, setLoading] = useState<boolean>(false)

  const { pasteInResponsesMutation, pasteInMatrixRowsMutation } =
    usePasteMutations()

  const pasteToMatrixRow: (
    text: string,
    matrixTitleLk: string,
    position: number,
    positionSelectedText?: PositionTextSelection
  ) => Promise<void> = async (
    text,
    matrixTitleLk,
    position,
    positionSelectedText
  ) => {
    setLoading(true)
    const pastedRows = splitTextIntoLinesAndCleanup(
      text,
      !!positionSelectedText?.startPositionSelectedText
    )

    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!pastedRows || pastedRows.length < 1) {
      // TODO: show some error message here?
      return
    }

    // ✅ Read from Apollo cache only when paste happens (lazy)
    const matrixItem = apolloClient.readFragment<DraftMatrixItem>({
      fragment: DRAFT_MATRIX_ITEM,
      fragmentName: 'DraftMatrixItem',
      id: `DraftMatrixItem:{"matrixTitleLk":"${matrixTitleLk}"}`
    })
    const matrixRows = matrixItem?.matrixRows ?? emptyArr

    let updatedRow = matrixRows[position].question?.text
    if (
      positionSelectedText &&
      typeof positionSelectedText.startPositionSelectedText === 'number' &&
      typeof positionSelectedText.endPositionSelectedText === 'number'
    ) {
      updatedRow = replacingTextAt(
        matrixRows[position].question?.text ?? '',
        pastedRows[0],
        positionSelectedText.startPositionSelectedText,
        positionSelectedText.endPositionSelectedText
      )
    }

    pastedRows.splice(0, 1, updatedRow ?? '')
    await pasteInMatrixRowsMutation(matrixTitleLk, pastedRows, position)

    setLoading(false)
  }

  const pasteToMatrixColumn = async ({
    text,
    matrixTitleLk,
    position,
    positionSelectedText
  }: {
    text: string
    matrixTitleLk: string
    position: number
    positionSelectedText?: PositionTextSelection
  }): Promise<void> => {
    setLoading(true)
    const responseOptionTexts = splitTextIntoLinesAndCleanup(
      text,
      !!positionSelectedText?.startPositionSelectedText
    )

    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!responseOptionTexts || responseOptionTexts.length < 1) {
      // TODO: show some error message here?
      return
    }

    // ✅ Read from Redux only when paste happens (lazy)
    const state = store.getState()
    const responseOptions =
      state.questionnaire.responseOptionsByQuestion[matrixTitleLk]

    let updatedResponseOptionAtPosition =
      responseOptions[position].responseOption?.value ?? ''

    if (
      positionSelectedText &&
      typeof positionSelectedText.startPositionSelectedText === 'number' &&
      typeof positionSelectedText.endPositionSelectedText === 'number'
    ) {
      updatedResponseOptionAtPosition = replacingTextAt(
        updatedResponseOptionAtPosition,
        responseOptionTexts[0],
        positionSelectedText.startPositionSelectedText,
        positionSelectedText.endPositionSelectedText
      )
    }

    // update UI with pasted response options before actual update on backend
    dispatch(
      responseOptionPasted({
        questionLk: matrixTitleLk,
        position,
        responseOptionTexts,
        positionSelectedText
      })
    )

    responseOptionTexts.splice(0, 1, updatedResponseOptionAtPosition)
    await pasteInResponsesMutation(matrixTitleLk, responseOptionTexts, position)

    setLoading(false)
  }

  return { pasteToMatrixRow, pasteToMatrixColumn, loading }
}

export default useCopyPasteMatrix
